import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Save, X } from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';

const ClientForm = ({ client, onSubmit, onCancel }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm({
    defaultValues: client || {
      fullName: '',
      phone: '',
      email: '',
      address: '',
      vehicleNumber: '',
      startDate: '',
      policyType: '',
      premium: '',
      earned: '',
      commission: ''
    }
  });

  const startDate = watch('startDate');
  const premium = watch('premium');

  // Auto-calculate renewal date when start date changes
  useEffect(() => {
    if (startDate) {
      const renewalDate = new Date(startDate);
      renewalDate.setFullYear(renewalDate.getFullYear() + 1);
      setValue('renewalDate', renewalDate.toISOString().split('T')[0]);
    }
  }, [startDate, setValue]);

  // Auto-calculate commission when premium changes
  useEffect(() => {
    if (premium) {
      const commission = parseFloat(premium) * 0.20; // 20% commission
      setValue('commission', commission.toFixed(2));
    }
  }, [premium, setValue]);

  // Reset form when client changes
  useEffect(() => {
    if (client) {
      reset(client);
    } else {
      reset({
        fullName: '',
        phone: '',
        email: '',
        address: '',
        vehicleNumber: '',
        startDate: '',
        policyType: '',
        premium: '',
        earned: '',
        commission: ''
      });
    }
  }, [client, reset]);

  const onFormSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      if (client) {
        // Update existing client
        await api.put(`/clients/${client.id}`, data);
        toast.success('Client updated successfully!');
      } else {
        // Create new client
        await api.post('/clients', data);
        toast.success('Client created successfully!');
      }
      onSubmit();
    } catch (error) {
      console.error('Error saving client:', error);
      toast.error('Failed to save client');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      {/* Client Information */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4">Client Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input
              type="text"
              {...register('fullName', { required: 'Full name is required' })}
              className={`form-input ${errors.fullName ? 'error' : ''}`}
              placeholder="Enter client's full name"
            />
            {errors.fullName && (
              <p className="text-danger-600 text-sm mt-1">{errors.fullName.message}</p>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Phone *</label>
            <input
              type="tel"
              {...register('phone', { 
                required: 'Phone number is required',
                pattern: {
                  value: /^(\+254|0)[0-9]{9}$/,
                  message: 'Invalid phone number format'
                }
              })}
              className={`form-input ${errors.phone ? 'error' : ''}`}
              placeholder="e.g., +254 700 123 456"
            />
            {errors.phone && (
              <p className="text-danger-600 text-sm mt-1">{errors.phone.message}</p>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              type="email"
              {...register('email', {
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Invalid email format'
                }
              })}
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder="client@example.com"
            />
            {errors.email && (
              <p className="text-danger-600 text-sm mt-1">{errors.email.message}</p>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Address</label>
            <input
              type="text"
              {...register('address')}
              className="form-input"
              placeholder="Enter client's address"
            />
          </div>
        </div>
      </div>

      {/* Vehicle Information */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4">Vehicle Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="form-group">
            <label className="form-label">Registration Number *</label>
            <input
              type="text"
              {...register('vehicleNumber', { required: 'Vehicle registration number is required' })}
              className={`form-input ${errors.vehicleNumber ? 'error' : ''}`}
              placeholder="e.g., KAA 123A"
            />
            {errors.vehicleNumber && (
              <p className="text-danger-600 text-sm mt-1">{errors.vehicleNumber.message}</p>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Start Date *</label>
            <input
              type="date"
              {...register('startDate', { required: 'Start date is required' })}
              className={`form-input ${errors.startDate ? 'error' : ''}`}
            />
            {errors.startDate && (
              <p className="text-danger-600 text-sm mt-1">{errors.startDate.message}</p>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Renewal Date</label>
            <input
              type="date"
              {...register('renewalDate')}
              className="form-input readonly-field"
              readOnly
            />
            <small className="text-gray-500 text-sm">Automatically calculated</small>
          </div>

          <div className="form-group">
            <label className="form-label">Policy Type *</label>
            <select
              {...register('policyType', { required: 'Policy type is required' })}
              className={`form-select ${errors.policyType ? 'error' : ''}`}
            >
              <option value="">Select Policy Type</option>
              <option value="Comprehensive">Comprehensive</option>
              <option value="Third Party">Third Party</option>
              <option value="Third Party Fire & Theft">Third Party Fire & Theft</option>
              <option value="Act Only">Act Only</option>
            </select>
            {errors.policyType && (
              <p className="text-danger-600 text-sm mt-1">{errors.policyType.message}</p>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Premium (KSh)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              {...register('premium')}
              className="form-input"
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Earned (KSh)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              {...register('earned')}
              className="form-input"
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Commission Earned (KSh)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              {...register('commission')}
              className="form-input"
              placeholder="0.00"
            />
            <small className="text-gray-500 text-sm">Auto-calculated as 20% of premium</small>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
        >
          <X className="h-4 w-4 mr-2" />
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn btn-primary"
        >
          {isSubmitting ? (
            <div className="spinner w-4 h-4 mr-2"></div>
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {isSubmitting ? 'Saving...' : (client ? 'Update Client' : 'Save Client')}
        </button>
      </div>
    </form>
  );
};

export default ClientForm;
